import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, Field, Input, Label, Text } from "@andrum/ui";
import { SignInSchema } from "@andrum/schemas";
import { signInWithPassword } from "@/features/auth/api/auth-api";
import { AuthMethods } from "@/features/auth/components/AuthMethods";
import { resolveHomePath } from "@/features/auth/lib/routing";
import { useAuthStore } from "@/stores/auth-store";
import { getSupabase } from "@/lib/supabase/client";
import { isAuthEnforced } from "@/lib/supabase/client";

export function LoginScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    const parsed = SignInSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("errorGeneric"));
      return;
    }

    if (!isAuthEnforced()) {
      void navigate({ to: "/app/today" });
      return;
    }

    setLoading(true);
    const result = await signInWithPassword(parsed.data);
    if (!result.ok) {
      setLoading(false);
      setError(result.message);
      return;
    }

    // Wait for session + profile (incl. onboarding_completed_at) before routing
    const supabase = getSupabase();
    const {
      data: { session },
    } = supabase
      ? await supabase.auth.getSession()
      : { data: { session: null } };
    if (session?.user) {
      await useAuthStore.getState().setSession({
        user: { id: session.user.id, email: session.user.email },
      });
    } else {
      await useAuthStore.getState().refreshProfile();
    }
    setLoading(false);

    void navigate({ to: resolveHomePath() });
  };

  return (
    <div className="flex h-dvh max-h-dvh flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain bg-background px-5 pt-[calc(env(safe-area-inset-top)+12px)] pb-[calc(env(safe-area-inset-bottom)+12px)] [-webkit-overflow-scrolling:touch]">
      <div className="flex shrink-0 flex-col items-center gap-2 pt-1">
        <img
          src="/app-icon-inner.svg"
          alt=""
          width={56}
          height={56}
          className="h-14 w-14 rounded-[16px] shadow-md"
          aria-hidden
        />
        <Text variant="title" as="h1" className="text-center text-xl">
          {t("auth.loginTitle")}
        </Text>
        <Text
          variant="muted"
          className="line-clamp-2 max-w-sm text-center text-sm leading-snug"
        >
          {t("auth.welcomeLead")}
        </Text>
      </div>

      {!isAuthEnforced() ? (
        <div className="mt-3 shrink-0 rounded-2xl border border-border bg-surface-muted px-3 py-2.5">
          <Text variant="subtle" className="text-xs">
            {t("auth.demoMode")}
          </Text>
          <Button
            fullWidth
            className="mt-2 min-h-10"
            onClick={() => void navigate({ to: "/app/today" })}
          >
            {t("auth.continueAsGuest")}
          </Button>
        </div>
      ) : null}

      <form
        className="mt-4 flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <Field className="!gap-1.5">
          <Label htmlFor="email" className="text-xs">
            {t("auth.email")}
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="du@exempel.se"
            className="min-h-11"
          />
        </Field>
        <Field className="!gap-1.5">
          <Label htmlFor="password" className="text-xs">
            {t("auth.password")}
          </Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="min-h-11"
          />
          <Link
            to="/forgot-password"
            className="inline-block pt-0.5 text-xs text-primary"
          >
            {t("auth.forgotPassword")}
          </Link>
        </Field>

        {error ? (
          <p className="text-center text-sm text-danger" role="alert">
            {error}
          </p>
        ) : null}

        <Button fullWidth type="submit" loading={loading} className="min-h-11">
          {t("auth.login")}
        </Button>

        <div>
          <AuthMethods mode="login" compact />
        </div>
      </form>

      <div className="shrink-0 space-y-2 pt-2 text-center">
        <Link to="/signup" className="text-sm font-medium text-primary">
          {t("auth.signup")}
        </Link>
        <Link
          to="/welcome"
          className="block text-xs text-foreground-muted"
        >
          {t("back")}
        </Link>
      </div>
    </div>
  );
}
